import {
    Header,
    HeaderButtons,
    OperatorBadge,
    BreadcrumbNav
} from "@/pages/assistants/ScenarioStepsPage/styles.ts";
import { Table } from "@/components/ui/Table";
import { Button } from "@/components/ui/Button.tsx";
import { useEffect, useState, useRef } from "react";
import { PlusLogo } from "@/assets/Logos.tsx";
import { OperatorContextMenu } from '@/components/OperatorContextMenu';
import { CustomTextarea } from "@/pages/assistants/ScenarioStepsPage/components/CustomTextarea.tsx";
import { ImageUploader } from "@/pages/assistants/ScenarioStepsPage/components/ImageUploader.tsx";
import { StepOperator, Scenario, Step, UpdateStepData } from '@/types/assistants';
import { stepApi } from '@/api/step';
import { Link, useNavigate, useParams } from "react-router-dom";
import { IoChevronForward } from "react-icons/io5";
import { scenarioApi } from "@/api/scenarios.ts";
import { fetchAssistant } from "@/store/assistantSlice.ts";
import { useAppDispatch, useAppSelector } from "@/hooks/redux.ts";
import { StepContextMenu } from "@/components/StepContextMenu.tsx";
import { Title } from "@/components/ui/Title.ts";
import { SelectAsLink } from "@/components/ui/SelectAsLink.tsx";
import { Flex } from "@/components/ui/FlexBox.ts";
import { AddRequestParamsModal } from "@/pages/assistants/ScenarioStepsPage/components/AddRequestParamsModal.tsx";
import {FormModal} from "@/pages/assistants/ScenarioStepsPage/components/FormModal.tsx";
import {Input} from "@/components/ui/Input.tsx";
import {EditStepNumber} from "@/pages/assistants/ScenarioStepsPage/components/EditStepNumber.tsx";
import { Toast } from "@/components/ui/Toast.tsx";
import { ToastState } from "@/components/auth/types/register.types.ts";
import * as yaml from 'js-yaml';

const voicenamesForEn = [
    { label: 'Aoede', value: 'Aoede' },
    { label: 'Fenrir', value: 'Fenrir' },
    { label: 'Leda', value: 'Leda' },
    { label: 'Orus', value: 'Orus' },
    { label: 'en-US-Casual-K', value: 'en-US-Casual-K' },
    { label: 'en-US-Chirp-HD-D', value: 'en-US-Chirp-HD-D' },
    { label: 'en-US-Chirp-HD-F', value: 'en-US-Chirp-HD-F' },
    { label: 'en-US-Chirp-HD-O', value: 'en-US-Chirp-HD-O' },
]

const voicenamesForRu = [
    { label: 'ru-RU-Standard-A', value: 'ru-RU-Standard-A' },
    { label: 'ru-RU-Standard-B', value: 'ru-RU-Standard-B' },
    { label: 'ru-RU-Standard-C', value: 'ru-RU-Standard-C' },
]

const logItems = [
    { value: 'user', label: 'user' },
    { value: 'assistant', label: 'assistant' },
    { value: 'system', label: 'system' },
    { value: 'file', label: 'file' }
]

const methodsItems = [
    { value: 'GET', label: 'GET' },
    { value: 'POST', label: 'POST' },
    { value: 'PUT', label: 'PUT' },
    { value: 'DELETE', label: 'DELETE' }
]

const useDebounce = (callback: (data: TTableDataItem) => void, delay: number) => {
    const timeoutRef = useRef<NodeJS.Timeout | null>(null);

    return (data: TTableDataItem) => {
        if (timeoutRef.current) clearTimeout(timeoutRef.current);

        timeoutRef.current = setTimeout(() => {
            callback(data);
        }, delay);
    };
};

export type TTableDataItem = {
    step: number,
    operator: StepOperator;
    stepContent: string;
    stepName: string;
    id?: number;
    canAddAbove: boolean;
    canAddBelow: boolean;
    langPrefix?: string;
    contentPrefix?: string;
    params?: Record<string, any>
}

const TableHeaders = [
    { text: 'Шаг', value: 'step', width: '100px' },
    { text: 'Оператор', value: 'operator', width: '110px' },
    { text: 'Содержание шага', value: 'stepContent', width: '100%' },
    { text: 'Имя шага', value: 'stepName', width: '170px' }
]

const getOperatorParams = (item: TTableDataItem) => {
    const isSpeech = item.operator === 'SPEECH';
    const isTranscribe = item.operator === 'TRANSCRIBE';
    const isLog = item.operator === 'LOG';
    const isRequest = item.operator === 'REQUEST';
    const isPrompt = item.operator.startsWith('PROMPT');
    const isTimer = item.operator === 'TIMER';

    if (isTranscribe) {
        return { lang: item.langPrefix };
    }
    if (isLog) {
        return { role: item.contentPrefix };
    }
    if (isSpeech) {
        return { lang: item.langPrefix, voice: item.contentPrefix };
    }
    if (isPrompt) {
        return { ...item.params };
    }
    if (isTimer) {
        return { step: item.params?.step || '' };
    }
    if (isRequest) {
        const params: Record<string, string | undefined> = {
            method: item.params?.method,
            headers: item.params?.headers && JSON.stringify(item.params?.headers.reduce((acc: Record<string, any>, obj: {key: string; value: string}) => {
                acc[obj.key] = obj.value;
                return acc;
            }, {})),
            json: item.params?.json ? JSON.stringify(item.params?.json.reduce((acc: Record<string, any>, obj: {key: string; value: string}) => {
                acc[obj.key] = obj.value;
                return acc;
            }, {})) : undefined
        };
        if (item.params?.method === 'POST' && item.params?.form?.length) {
            params.form = JSON.stringify(item.params?.form.reduce((acc: Record<string, any>, obj: {key: string; value: string}) => {
                acc[obj.key] = obj.value;
                return acc;
            }, {}));
        }
        return params;
    }
    return undefined;
};

export const ScenarioStepsPage = () => {
    const [data, setData] = useState<Array<TTableDataItem>>([]);
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [scenario, setScenario] = useState<Scenario | null>(null)
    const [availablePrompts, setAvailablePrompts] = useState<StepOperator[]>([]);
    const creatingNumberRef = useRef<number | null>(null);
    const delayedEdit =  useRef<TTableDataItem | null>(null);
    const fileInputRef = useRef<HTMLInputElement>(null);
    const [toast, setToast] = useState<ToastState>({
        show: false,
        message: '',
        type: 'success'
    });
    const { scenarioId, id } = useParams();
    const dispatch = useAppDispatch();
    const navigate = useNavigate();
    const { assistant } = useAppSelector((state: any) => state.assistant);

    const createStep = async (updatedData: TTableDataItem[], item: TTableDataItem) => {
        if (!item.id && !delayedEdit.current && !creatingNumberRef.current) {
            creatingNumberRef.current = item.step;
            const numberMatch = item.operator.match(/^PROMPT_(\d+)$/);
            const content = item.stepContent;

            const body: Record<string, any> = {
                step: item.step,
                operator: numberMatch ? "PROMPT" : item.operator,
                content,
                name: item.stepName,
                assistant_id: Number(id),
                scenario_id: Number(scenarioId),
                context: numberMatch ? Number(numberMatch[1]) : 0,
                params: getOperatorParams(item)
            };

            stepApi.create(body).then((res) => {
                const editedItemIndex = updatedData.findIndex((i) => i.step === item.step);
                const temp = [...updatedData];
                if (delayedEdit.current) {
                    const newEditBody = {...delayedEdit.current, id: res.data.id };
                    temp[editedItemIndex] = newEditBody;
                    setData(temp);
                    debouncedEdit(newEditBody);
                    delayedEdit.current = null;
                } else {
                    if (item.operator === 'PROMPT') {
                        setAvailablePrompts((prevState) => [...prevState, `PROMPT_${item.step}`]);
                    }
                    temp[editedItemIndex] = { ...temp[editedItemIndex], id: res.data.id };
                    setData(temp);
                }
            }).finally(() => {
                creatingNumberRef.current = null;
            });
        }
    };

    const debouncedEdit = useDebounce((data: TTableDataItem) => {
        if (data.id) {
            const numberMatch = data.operator.match(/^PROMPT_(\d+)$/);
            const body: UpdateStepData = {
                id: data.id,
                name: data.stepName,
                assistant_id: Number(id),
                scenario_id: Number(scenarioId),
                operator: numberMatch ? "PROMPT" : data.operator,
                content: data.stepContent,
                step: data.step,
                step_number: data.step,
                context: numberMatch ? Number(numberMatch[1]) : 0,
                params: getOperatorParams(data)
            };
            stepApi.update(body);
        }
    }, 500);

    const addRow = () => {
        const prevStep = (data[data.length - 1]?.step || 0) + 100
        const newStep: TTableDataItem = {
            step: prevStep,
            operator: 'PRINT',
            stepContent: '',
            stepName: '',
            canAddAbove: true,
            canAddBelow: true
        }
        const updatedData = [...data, newStep];
        setData(updatedData)
        createStep(updatedData, newStep);
    }

    const handleChange = (key: number, column: string, value: string | number) => {
        const editedItemIndex = data.findIndex(i => i.step === key);
        const temp = [...data];

        if(temp[editedItemIndex].operator === 'PROMPT' && value !== 'PROMPT') {
            setAvailablePrompts((prevState) =>
                prevState.filter(prompt => prompt !== `PROMPT_${key}`)
            );
        }
        if(value === 'PROMPT' && temp[editedItemIndex].operator !== 'PROMPT') {
            setAvailablePrompts((prevState) => ([...prevState, `PROMPT_${key}`]));
        }

        if(column === 'operator' && value === 'LOG') {
            temp[editedItemIndex].contentPrefix = 'assistant'
        }

        temp[editedItemIndex] = { ...temp[editedItemIndex], [column]: value };
        setData(temp);
        if (creatingNumberRef.current === key) {
            delayedEdit.current = temp[editedItemIndex]
        } else if (temp[editedItemIndex].id) {
            debouncedEdit(temp[editedItemIndex]);
        }
    };

    const handleChangeParams = (step: number, column: string, value: any, min?: number, max?:number) => {
        const editedItemIndex = data.findIndex(i => i.step === step);
        const temp = [...data];
        const params = { ...temp[editedItemIndex].params, [column]: value }
        if(column === 'method' && value !== 'POST') {
            params.form = []
        }
        temp[editedItemIndex] = { ...temp[editedItemIndex], params };
        setData(temp);
        const isValidRange = (min === undefined || value >= min) && (max === undefined || value <= max);

        if (isValidRange) {
            if (creatingNumberRef.current === step) {
                delayedEdit.current = temp[editedItemIndex];
            } else if (temp[editedItemIndex].id) {
                debouncedEdit(temp[editedItemIndex]);
            }
        }
    };

    const handleChangeLanguage = (value: string, key: number) => {
        const editedItemIndex = data.findIndex(i => i.step === key);
        const temp = [...data];
        temp[editedItemIndex] = {
            ...temp[editedItemIndex],
            langPrefix: value,
            contentPrefix: undefined
        }
        setData(temp);
        if(temp[editedItemIndex].id) {
            debouncedEdit(temp[editedItemIndex]);
        }
    }

    const handleChangeVoiceover = (value: string, key: number) => {
        const editedItemIndex = data.findIndex(i => i.step === key);
        const temp = [...data];
        temp[editedItemIndex] = {
            ...temp[editedItemIndex],
            contentPrefix: value,
        }
        setData(temp);
        if(temp[editedItemIndex].id) {
            debouncedEdit(temp[editedItemIndex]);
        }
    }

    const getTableData = async () => {
        if (scenarioId) {
            const { data } = await stepApi.getOne(parseInt(scenarioId));
            const mappedData = data.map((i: Step, index: number) => {
                if(i.Operator === "PROMPT" && i.Context === 0) {
                    setAvailablePrompts((prevState) => ([...prevState, `PROMPT_${i.Step}`]));
                }
                const prevItem = data[index - 1];
                const nextItem = data[index + 1]
                const isSpeech = i.Operator === 'SPEECH';
                const isLog = i.Operator === 'LOG'
                const isRequest = i.Operator === 'REQUEST'
                const isPrompt = i.Operator.startsWith('PROMPT')
                const isTimer = i.Operator === 'TIMER'

                const step: TTableDataItem = {
                    step: i.Step,
                    operator: i.Operator === "PROMPT"
                        ? (`PROMPT${i.Context === 0 ? '' : '_' + i.Context}` as StepOperator)
                        : (i.Operator as StepOperator),
                    stepContent: i.Content,
                    stepName: i.Name,
                    id: i.ID,
                    canAddAbove: false,
                    canAddBelow: false,
                    langPrefix: i.Params?.lang || undefined,
                }

                if(isSpeech) {
                    const { voice } = i.Params
                    step.contentPrefix = voice;
                }

                if(isLog) {
                    const { role } = i.Params
                    step.contentPrefix = role;
                }

                if(isPrompt) {
                    step.params = {
                        ...i.Params
                    }
                }

                if(isTimer) {
                    step.params = {
                        step: i.Params?.step || ''
                    }
                }

                if(isRequest) {
                    step.params = {
                        method: i.Params.method,
                        json: i.Params.json ? Object.entries(JSON.parse(i.Params.json)).map(([key, value]) => ({ value, key })) : undefined,
                        form: i.Params.form ? Object.entries(JSON.parse(i.Params.form)).map(([key, value]) => ({ value, key }))  : undefined,
                        headers: i.Params.headers ? Object.entries(JSON.parse(i.Params.headers)).map(([key, value]) => ({ value, key })) : undefined,
                    }
                }

                if((!prevItem && i.Step > 0) || (prevItem && (prevItem.Step + 1 !== i.Step))) {
                    step.canAddAbove = true;
                }
                if(!nextItem || (nextItem && (nextItem.Step - 1 !== i.Step ))){
                    step.canAddBelow = true;
                }
                return step;
            })
            setData(mappedData)
        }
    }

    const onDelete = async (id: number | undefined, step: number) => {
        if(id) {
            try {
                const deletableIndex = data.findIndex(i => i.id === id);
                const temp = [...data];
                if(temp[deletableIndex + 1]) {
                    temp[deletableIndex + 1].canAddAbove = true;
                }
                if(temp[deletableIndex - 1]) {
                    temp[deletableIndex - 1].canAddBelow = true;
                }
                setData(temp.filter(i => i.id !== id));
                await stepApi.delete(parseInt(scenarioId || '0'), id);
            } catch (error) {
                console.error('Error deleting step:', error);
            }
        } else {
            const deletableIndex = data.findIndex(i => i.step === step);
            const temp = [...data];
            if(temp[deletableIndex + 1]) {
                temp[deletableIndex + 1].canAddAbove = true;
            }
            if(temp[deletableIndex - 1]) {
                temp[deletableIndex - 1].canAddBelow = true;
            }
            setData(temp.filter(i => i.step !== step));
        }
    };

    const onAddAbove = (item: TTableDataItem) => {
        const editableIndex = data.findIndex(i => i.step === item.step);
        const temp = [...data];
        const prevItem = temp[editableIndex - 1];
        const newValue = Math.round(((prevItem?.step || 0) + item.step) / 2)
        const canAddAbove = newValue - 1 !== 0 && !temp.some(i => i.step === item.step - 2);
        if(!canAddAbove && editableIndex !== 0) {
            temp[editableIndex - 1].canAddBelow = false;
        }
        if(newValue + 1 === item.step) {
            temp[editableIndex].canAddAbove = false;
        }
        const newItem = {
            step: newValue,
            operator: 'PRINT' as StepOperator,
            stepContent: '',
            stepName: '',
            canAddAbove: canAddAbove,
            canAddBelow: newValue + 1 !== item.step,
        }
        temp.splice(editableIndex, 0, newItem);
        setData(temp);
        createStep(temp, newItem)
    }

    const onAddBelow = (item: TTableDataItem) => {
        const editableIndex = data.findIndex(i => i.step === item.step);
        const temp = [...data];
        const nextItem = temp[editableIndex + 1];
        const newValue = Math.round(((nextItem?.step || item.step + 100) + item.step) / 2)
        const canAddBelow = !temp.some(i => i.step === item.step + 2);
        if(!canAddBelow) {
            temp[editableIndex + 1].canAddAbove = false;
        }
        if(newValue - 1 === item.step) {
            temp[editableIndex].canAddBelow = false;
        }
        const newItem = {
            step: newValue,
            operator: 'PRINT' as StepOperator,
            stepContent: '',
            stepName: '',
            canAddAbove: newValue - 1 === item.step,
            canAddBelow
        }
        temp.splice(editableIndex + 1, 0, newItem);
        setData(temp);
        createStep(temp, newItem)
    }

    const duplicateStep = (item: TTableDataItem) => {
        const editableIndex = data.findIndex(i => i.step === item.step);
        const temp = [...data];
        const nextItem = temp[editableIndex + 1];
        const newValue = Math.round(((nextItem?.step || item.step + 100) + item.step) / 2)
        const canAddBelow = !temp.some(i => i.step === item.step + 2);
        if(!canAddBelow) {
            temp[editableIndex + 1].canAddAbove = false;
        }
        if(newValue - 1 === item.step) {
            temp[editableIndex].canAddBelow = false;
        }
        const newItem = { ...item, step: newValue, id: undefined }
        temp.splice(editableIndex + 1, 0, newItem);
        setData(temp);
        createStep(temp, newItem)
    }

    const getScenario = () => {
        if (scenarioId) {
            scenarioApi.getOne(parseInt(scenarioId))
                .then((res: any) => setScenario(res.data))
                .finally(() => setIsLoading(false));
        }
    }

    const handleImportFromFile = () => {
        if (fileInputRef.current) {
            fileInputRef.current.click();
        }
    };

    const handleFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files ? event.target.files[0] : null;
        if (!file) return;

        try {
            const fileContent = await file.text();
            const yamlData = yaml.load(fileContent) as any;
            
            if (yamlData && scenarioId && id) {
                const payload = {
                    scenario_id: Number(scenarioId),
                    assistant_id: Number(id),
                    yaml_content: fileContent,
                };

                scenarioApi.updateFromYaml(payload).then(() => {
                    setToast({
                        show: true,
                        message: 'Сценарий успешно обновлен из файла',
                        type: 'success'
                    });
                    // Reload the steps data
                    getTableData();
                }).catch(() => {
                    setToast({
                        show: true,
                        message: 'Не удалось обновить сценарий из файла',
                        type: 'error'
                    });
                });
            } else {
                setToast({
                    show: true,
                    message: 'Неверный формат файла',
                    type: 'error'
                });
            }
        } catch (e) {
            setToast({
                show: true,
                message: 'Ошибка при парсинге файла',
                type: 'error'
            });
        }

        // Clear the file input
        if (fileInputRef.current) {
            fileInputRef.current.value = '';
        }
    };

    useEffect(() => {
        setIsLoading(true);
        getTableData();
        getScenario();
    }, [])

    useEffect(() => {
        if(id) {
            dispatch(fetchAssistant(Number(id)))
        }
    }, [id]);

    return (
        <div style={{ paddingBottom: '150px' }}>
            {toast.show && (
                <Toast
                    message={toast.message}
                    type={toast.type}
                    onClose={() => setToast(prev => ({ ...prev, show: false }))}
                />
            )}
            <Header>
                <Title>Добавить сценарий</Title>
                <HeaderButtons>
                    <Button
                        onClick={handleImportFromFile}
                        variant="primary"
                        padding="10px 25px 10px 30px"
                    >
                        Выбрать с диска
                    </Button>
                    <input
                        type="file"
                        ref={fileInputRef}
                        style={{ display: 'none' }}
                        accept=".yaml,.yml"
                        onChange={handleFileChange}
                    />
                    <Button
                        onClick={() => navigate(
                            `/dashboard/assistants/${id}/scenarios/${scenarioId}`
                        )}
                        variant="blue"
                        padding="10px 25px 10px 30px"
                    >
                        Настройки сценария
                    </Button>
                    <Button variant="green" padding="10px 25px">Run</Button>
                </HeaderButtons>
            </Header>
            <BreadcrumbNav>
                <Link to="/dashboard/assistants">Мои ассистенты</Link>
                <IoChevronForward size={14}/>
                <Link to={`/dashboard/assistants/${assistant?.ID}/scenarios`}>{assistant?.Name}</Link>
                <IoChevronForward size={14}/>
                <span>{scenario?.Name}</span>
            </BreadcrumbNav>
            <Table loading={isLoading} headers={TableHeaders} data={data} tableKey='step'>
                {({item, value, column, key}) => {
                    if(column === 'step') {
                        return (
                            <EditStepNumber value={String(value)} setValue={(val) => handleChange(key, column, val)} tableData={data} />
                        )
                    }
                    if (column === 'operator') {
                        return (
                            <div style={{position: 'relative'}}>
                                <div style={{display: 'flex', alignItems: 'center', gap: '4px'}}>
                                    <OperatorBadge
                                        operator={value as StepOperator}
                                        style={{cursor: 'pointer'}}
                                        className="operator-badge"
                                    >
                                        {String(value)}
                                    </OperatorBadge>
                                    <OperatorContextMenu
                                        currentOperator={value as StepOperator}
                                        availablePromptOperators={availablePrompts}
                                        step={key}
                                        disabled={value === 'PICTURE' && !!item.stepContent}
                                        onSelect={(value) => {
                                            handleChange(key, column, value)
                                        }}
                                    />
                                </div>
                            </div>
                        )
                    }
                    if (column === 'stepContent') {
                        const commonTextareaProps = {
                            value: value as string,
                            setValue: (value: string) => handleChange(key, column, value),
                            placeholder: 'Step content'
                        };
                        if(item.operator === 'CODE') {
                            return (
                                <div style={{ fontFamily: 'Roboto Mono' }}>{value as string}</div>
                            )
                        }
                        if (item.operator === 'SPEECH' || item.operator === 'TRANSCRIBE' || item.operator === 'LOG' || item.operator === 'REQUEST' || item.operator === 'TIMER') {
                            return (
                                <div style={{ display: 'flex', position: 'relative', lineHeight: '25px' }}>
                                    <CustomTextarea {...commonTextareaProps} />
                                    {item.operator === 'LOG' && (
                                        <SelectAsLink
                                            items={logItems}
                                            label='Role'
                                            value={item.contentPrefix}
                                            onSelect={(value) => handleChange(key, 'contentPrefix', String(value))}
                                        />
                                    )}
                                    {(item.operator === 'TRANSCRIBE' || item.operator === 'SPEECH') && (
                                        <SelectAsLink
                                            items={[{ value: 'ru-RU', label: 'ru-RU' }, { value: 'en-US', label: 'en-US' }]}
                                            label='Language'
                                            value={item.langPrefix}
                                            onSelect={(value) => handleChangeLanguage(String(value), key)}
                                        />
                                    )}
                                    {item.operator === 'SPEECH' && (
                                        <div style={{ position: 'relative', marginLeft: '8px' }}>
                                            <SelectAsLink
                                                items={item.langPrefix === 'ru-RU' ? voicenamesForRu : voicenamesForEn}
                                                label='Voicename'
                                                value={item.contentPrefix}
                                                disabled={!item.langPrefix}
                                                onSelect={(value) => handleChangeVoiceover(String(value), key)}
                                            />
                                        </div>
                                    )}
                                    {item.operator === 'TIMER' && (
                                        <div style={{ position: 'relative', marginLeft: '8px' }}>
                                            <FormModal label='Step' modalTitle='Timer Step Parameter' value={item.params?.step ?? ''}>
                                                <Input
                                                    style={{ width: '300px' }}
                                                    placeholder='Step to start from (can be blank)'
                                                    value={item.params?.step ?? ''}
                                                    onChange={(e) => {
                                                        handleChangeParams(key, 'step', e.target.value);
                                                    }}
                                                />
                                            </FormModal>
                                        </div>
                                    )}
                                    {item.operator === 'REQUEST' && (
                                        <Flex style={{ gap: '8px' }}>
                                            <div style={{ position: 'relative' }}>
                                                <SelectAsLink
                                                    items={methodsItems}
                                                    label='Method'
                                                    value={item.params?.method}
                                                    onSelect={(value) => handleChangeParams(key, 'method', value)}
                                                />
                                            </div>
                                            <AddRequestParamsModal
                                                label='Headers'
                                                modalTitle='REQUEST Headers'
                                                key={item.step + 'headers'}
                                                initialValue={item.params?.headers}
                                                onChange={(value) => handleChangeParams(key, 'headers', value)}
                                            />
                                            {item.params?.method === 'POST' && (
                                                <AddRequestParamsModal
                                                    key={item.step + 'form'}
                                                    label='Form vars'
                                                    disabled={item.params?.json?.length}
                                                    modalTitle='REQUEST Form vars'
                                                    onChange={(value) => handleChangeParams(key, 'form', value)}
                                                    initialValue={item.params?.form}
                                                />
                                            )}
                                            <AddRequestParamsModal
                                                key={item.step + 'json'}
                                                label='JSON vars'
                                                disabled={item.params?.form?.length}
                                                modalTitle='REQUEST JSON vars'
                                                initialValue={item.params?.json}
                                                onChange={(value) => handleChangeParams(key, 'json', value)}
                                            />
                                        </Flex>
                                    )}
                                </div>
                            );
                        }

                        if(item.operator.startsWith('PROMPT')) {
                            return (
                                <Flex>
                                    <CustomTextarea {...commonTextareaProps} />
                                    <FormModal label='Context Window' value={item.params?.context_window ?? ''} modalTitle='Context Window'>
                                        <Input
                                            type='number'
                                            min={500}
                                            max={30000}
                                            style={{ width: '300px' }}
                                            step={1}
                                            value={item.params?.context_window ?? ''}
                                            onChange={(e) => {
                                                const intValue = e.target.value.replace(/\D/g, "");
                                                if(intValue) {
                                                    handleChangeParams(key, "context_window", String(intValue), 500, 30000);
                                                }
                                            }}
                                            onBlur={(e) => {
                                                const intValue = e.target.value.replace(/\D/g, "");
                                                if (Number(intValue) <= 500) {
                                                    handleChangeParams(key, 'context_window', String(500));
                                                } else if (Number(intValue) >= 30000) {
                                                    handleChangeParams(key, 'context_window', String(30000));
                                                }
                                            }}
                                        />
                                    </FormModal>
                                    <div style={{ position: 'relative', margin: '0 8px', height: 'fit-content' }}>
                                        <SelectAsLink
                                            items={[{ label: 'DeepSeek', value: 'deepseek' }, { label: 'Opus', value: 'opus' }]}
                                            label='Model'
                                            value={item.params?.model ?? 'deepseek'}
                                            onSelect={(value) => handleChangeParams(key, 'model', value)}
                                        />
                                    </div>
                                    <div style={{ position: 'relative', margin: '0 8px', height: 'fit-content' }}>
                                        <SelectAsLink
                                            items={[{ label: 'user', value: 'user' }, { label: 'system', value: 'system' }]}
                                            label='Role'
                                            value={item.params?.role}
                                            onSelect={(value) => handleChangeParams(key, 'role', value)}
                                        />
                                    </div>
                                    <FormModal label='Temperature' modalTitle='Temperature' value={item.params?.temperature ?? ''}>
                                        <Input
                                            type='number'
                                            min={0.1}
                                            step={0.1}
                                            max={1}
                                            style={{ width: '300px' }}
                                            value={item.params?.temperature ?? ''}
                                            onChange={(e) => {
                                                const value = e.target.value;
                                                if (/^\d*\.?\d{0,1}$/.test(value)) {
                                                    handleChangeParams(key, 'temperature', String(value), 0.1, 1);
                                                }
                                            }}
                                            onBlur={(e) => {
                                                const intValue = e.target.value;
                                                if (Number(intValue) <= 0.1) {
                                                    handleChangeParams(key, 'temperature', String(0.1));
                                                } else if (Number(intValue) >= 1) {
                                                    handleChangeParams(key, 'temperature', String(1));
                                                }
                                            }}
                                        />
                                    </FormModal>
                                </Flex>
                            )
                        }

                        if (item.operator === 'PICTURE') {
                            return (
                                <ImageUploader
                                    value={item.stepContent}
                                    onChange={(value: string) => handleChange(key, column, value)}
                                    handleDelete={() => handleChange(key, column, '')}
                                    acceptType='image'
                                />
                            );
                        }

                        if (item.operator === 'VIDEO') {
                            return (
                                <ImageUploader
                                    value={item.stepContent}
                                    onChange={(value: string) => handleChange(key, column, value)}
                                    handleDelete={() => handleChange(key, column, '')}
                                    acceptType='video'
                                />
                            );
                        }

                        return (
                            <CustomTextarea
                                {...commonTextareaProps}
                                showExpand={item.operator === 'DATA'}
                                style={{ fontFamily: ['SCRIPT', 'CODE'].includes(item.operator) ? 'Roboto Mono' : 'Open Sans' }}
                            />
                        );
                    }
                    if(column === 'stepName') {
                        return (
                            <div style={{ display: 'flex', position: 'relative' }}>
                                <CustomTextarea
                                    value={value as string}
                                    setValue={(value: string) => handleChange(key, column, value)}
                                    placeholder='Step name'
                                />
                                <StepContextMenu
                                    onAddAbove={() => onAddAbove(item)}
                                    onAddBelow={() => onAddBelow(item)}
                                    duplicateStep={() => duplicateStep(item)}
                                    onDelete={() => onDelete(item.id, item.step)}
                                    step={item.step as number}
                                    canAddAbove={item.canAddAbove}
                                    canAddBelow={item.canAddBelow}
                                />
                            </div>
                        )
                    }
                    return <span style={{ lineHeight: '26px' }}>{String(value)}</span>;
                }}
            </Table>
            <div style={{ display: 'flex', justifyContent: 'end', marginTop: '22px', marginBottom: '16px' }}>
                <Button variant="primary" onClick={addRow}>
                    <PlusLogo/>
                </Button>
            </div>
        </div>
    )
}
